<script setup lang="ts">
import { Check, Filter, Search } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Selection } from '@/types/configuration';
import type { Filters } from '@/types/filters';

import { useEditor } from '@/composables/useEditor';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useProductsStore } from '@/stores/useProductsStore';

import TheCard from '../mode-list/TheCard.vue';
import Modal from './Modal.vue';
import SelectionConfiguration from './SelectionConfiguration.vue';

const emits = defineEmits(['save']);
const globalConfigurationStore = useGlobalConfigurationStore();
const { globalConfiguration } = storeToRefs(globalConfigurationStore);

const productsStore = useProductsStore();
const { products } = storeToRefs(productsStore);

const { configurationManual: configuration, overrideMainActionButton, configToFilter } = useEditor();
const { t } = useI18n();

const prefixUrl = computed(() => globalConfiguration.value?.prefixUrl ?? null);

let searchDebounce = 0;
const $root = ref<HTMLElement>();
const $fetchMore = ref<HTMLElement>();
const search = ref('');
const loading = ref(true);
const showConfigModal = ref(false);
const tmpConfig = ref<Selection>({ // Used to store the config while editing without modifying the actual config object
  products: [],
  taxonomies: [],
});
const config = ref<Selection>({
  products: [],
  taxonomies: [],
});
const intersectionObserver = ref<IntersectionObserver>();

const filters = computed<Filters>(() => {
  const filters: Filters = {
    size: 20,
    sorts: [],
    filters: [{
      filterProperties: {
        type: 'publication_states',
        target: 'product',
      },
      selection: ['PUBLISHED'],
    }],
    metrics: [
      {
        type: 'taxonomyCount',
        taxonomyTypes: [
          'TYPES',
          'THEMES',
          'PLACES',
        ],
      },
    ],
    selections: [],
    properties: [],
  };

  if (search.value.length > 0) {
    filters.sorts = [
      {
        type: 'search',
        query: search.value,
      },
    ];
  }
  else {
    filters.sorts = [];
  }

  return filters;
});

function openFiltersModal() {
  tmpConfig.value = JSON.parse(JSON.stringify(config.value));
  showConfigModal.value = true;
}

function saveFilters() {
  config.value = JSON.parse(JSON.stringify(tmpConfig.value));
  filters.value.filters = configToFilter(config.value);
  showConfigModal.value = false;
}

function toggleProduct(productId: string) {
  if (configuration.value.products.includes(productId)) {
    configuration.value.products = configuration.value.products.filter(it => it !== productId);
  }
  else {
    configuration.value.products = [...configuration.value.products, productId];
  }
}

async function loadMore() {
  loading.value = true;
  try {
    await productsStore.fetchPaginatedProductsList(filters.value);
  }
  catch (e) {
    console.error(e);
  }
  finally {
    loading.value = false;
  }
}

async function updateFilters() {
  loading.value = true;
  await productsStore.fetchProductsList(filters.value);
  loading.value = false;
}

onMounted(async () => {
  overrideMainActionButton.value = {
    text: t('editor.selection.validate'),
    action() {
      emits('save');
    },
  };
  await updateFilters();

  const root = $root.value;
  const fetchMore = $fetchMore.value;
  if (fetchMore == null) {
    throw new Error('Could not find fetch more element !');
  }
  if (root == null) {
    throw new Error('Could not find root element !');
  }
  intersectionObserver.value = new IntersectionObserver((entries: Array<IntersectionObserverEntry>) => {
    if (entries.find(it => it.isIntersecting)) {
      loadMore();
    }
  }, { root });
  intersectionObserver.value.observe(fetchMore);
});

onUnmounted(() => {
  intersectionObserver.value?.disconnect();
  overrideMainActionButton.value = undefined;
});

watch(filters, () => {
  clearTimeout(searchDebounce);
  searchDebounce = window.setTimeout(updateFilters, 300);
}, { deep: true });
</script>

<template>
  <div class="product-selector">
    <div class="search-container">
      <div class="text-search">
        <b>{{ $t('editor.do_manual_selection') }}</b>
        <div class="search">
          <Search class="search-icon" />
          <input
            v-model="search" type="text" :placeholder="$t('editor.search_product')"
            :aria-label="t('editor.search_product')"
          >
        </div>
      </div>
      <button class="filter-button" @click="openFiltersModal">
        <Filter :size="14" color="white" />
        {{ $t('menu.filters') }}
      </button>
    </div>
    <div ref="$root" class="cards">
      <div
        v-for="product in products" :key="product.id" role="button" tabindex="0" class="card"
        :data-selected="configuration.products.includes(product.id)" @click="toggleProduct(product.id)"
        @keydown.enter="toggleProduct(product.id)"
      >
        <TheCard
          :style="{ minWidth: '300px', pointerEvents: 'none' }"
          :product
          :responsive-row="false"
          :show-favorite="false"
          :prefix-url="prefixUrl"
        />
        <div v-if="configuration.products.includes(product.id)" class="card-check">
          <div class="card-check-inner">
            <Check />
          </div>
        </div>
      </div>
      <div ref="$fetchMore" />
    </div>
    <Modal v-if="showConfigModal" width="60vw" height="60vh" @close="showConfigModal = false">
      <template #title>
        {{ $t('menu.filters') }}
      </template>

      <SelectionConfiguration v-model:config="tmpConfig" />

      <template #footer>
        <div class="spacer">
          <span
            role="button" tabindex="0" class="action" @click="tmpConfig.taxonomies = []"
            @keydown.enter="tmpConfig.taxonomies = []"
          >{{ $t('editor.delete_all') }}</span>
          <button @click="saveFilters">
            {{ $t('editor.show_results') }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<style lang="css" scoped>
.product-selector {
  max-height: 900px;
}

.cards {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 1rem;
  padding-top: 1rem;
}

.card {
  position: relative;
  width: 300px;
  cursor: pointer;
}

.card[data-selected='true'] {
  outline: var(--yb-color-primary) 3px solid;
}

.card[data-selected='true'] > *:first-child {
  opacity: 0.6;
}

.card .card-check {
  position: absolute;
  top: 1rem;
  right: 1rem;
  border: 1px solid var(--yb-color-primary);
  border-radius: 50%;
  background: white;
  padding: 2px;
  display: flex;
  justify-content: center;
  align-items: center;

  .card-check-inner {
    background: var(--yb-color-primary);
    color: white;
    border-radius: 50%;
    --size: 1.5rem;
    width: var(--size);
    height: var(--size);
    display: flex;
    justify-content: center;
    align-items: center;

    & > * {
      width: 1rem;
    }
  }
}

.search {
  position: relative;
  display: flex;
  border: 1px solid #bbb;
  border-radius: 8px;

  .search-icon {
    position: absolute;
    top: 50%;
    left: 1.4rem;
    transform: translate(-50%, -50%);
    width: 1.3rem;
  }

  input {
    background: transparent;
    border: none;
    width: 100%;
    min-height: 2.5rem;
    color: black;
    font-size: 0.9rem;
    padding-left: 3rem;
    border-radius: 8px;
    outline: none;
  }

  input:focus {
    outline: 2px solid var(--yb-color-primary);
  }

  input::placeholder {
    color: #aaa;
  }
}

.search-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.text-search {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 40%;
}

.filter-button {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.8rem 1rem;
  background: #252525;
  border: none;
  border-radius: 10px;
  color: white;
  font-weight: 600;
  gap: 0.4rem;
  cursor: pointer;
  font-size: 0.9rem;
}
</style>
